import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  IconButton,
  TextField,
} from '@mui/material'
import { FC, useState } from 'react'
import DenyIcon from '@/assets/icons/actions/deny.svg'
import CustomImageCard from '../../card/CustomImageCard'
import DeleteIcon from '@mui/icons-material/Delete'
import { uploadFile } from '@/api'

type Attachment = {
  id: number
  fileName: string
  presignedUrl: string
  fileType: string
  attachmentType: string
}

interface PurchaseOrderDataProps {
  data: {
    attachments: Attachment[]
    retainAttachmentIds?: number[]
    purchaseOrderNumber: string
  }
  ticketId: number
  index: number
  purchaseDataIndex: number
  onChange: (
    parentIndex: number,
    purchaseDataIndex: number,
    field: string,
    value: string | number,
  ) => void
  onDelete: (index: number, parentIndex: number) => void
  handleAddAttachment: (
    index: number,
    value: Attachment,
    parentIndex: number,
  ) => void
  handleDeleteAttachment: (
    parentIndex: number,
    purchaseDataIndex: number,
    attachmentIndex: number,
  ) => void
  disabled: boolean
}

export const PurchaseOrderData: FC<PurchaseOrderDataProps> = ({
  ticketId,
  handleAddAttachment,
  handleDeleteAttachment,
  index,
  purchaseDataIndex,
  data,
  onDelete,
  onChange,
  disabled,
}) => {
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    setLoading(true)
    if (!file) {
      console.warn('No file selected')
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    try {
      const { data } = await uploadFile({
        ticketId: ticketId,
        attachmentType: 'PO_QUOTATION',
        formData,
      })

      const { id, fileName } = data.data.attachment
      const { presignedUrl, fileType } = data.data

      handleAddAttachment(
        purchaseDataIndex,
        {
          id,
          fileName,
          presignedUrl,
          attachmentType: 'PO_QUOTATION',
          fileType: fileType,
        },
        index,
      )
    } catch (error) {
      console.error('File upload failed:', error)
    } finally {
      setLoading(false)
      e.target.value = ''
    }
  }
  const [loading, setLoading] = useState(false)
  return (
    <Grid2
      container
      gap={2}
      justifyContent="space-evenly"
      borderRadius={2}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr .2fr auto' },
      }}
    >
      {/* Delete Button  */}
      <Grid2 container alignItems="flex-start" gap={0}>
        <IconButton
          disabled={disabled}
          sx={{
            visibility: 'hidden',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          <img src={DenyIcon} alt="Delete" width={24} height={24} />
        </IconButton>
      </Grid2>

      {/* Purchase Request Number */}
      <Grid2 marginBottom={disabled ? 4 : 0}>
        <TextField
          label="PO Number"
          value={data.purchaseOrderNumber}
          size="small"
          fullWidth
          disabled={disabled}
          onChange={(e) => {
            onChange(
              index,
              purchaseDataIndex,
              'purchaseOrderNumber',
              e.target.value,
            )
          }}
        />
      </Grid2>

      {/* Attach Button */}
      {!disabled && (
        <Grid2
          container
          direction="row"
          alignItems="flex-start"
          sx={{ height: { xs: 'auto', sm: 100, md: 100 } }}
          minWidth={160}
          maxWidth={160}
        >
          <Button
            variant="contained"
            component="label"
            sx={{ width: '100%', background: '#484554', minWidth: 100 }}
            disabled={disabled}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Attach'
            )}
            <input
              type="file"
              hidden
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileUpload}
            />
          </Button>
        </Grid2>
      )}

      {/* Attachments */}
      <Grid2>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: { xs: 'auto', sm: 50, md: 40 },
            width: '100%',
            minWidth: '140px',
          }}
        >
          {(Array.isArray(data.attachments) &&
            data.attachments.map((item, attachmentIndex) => (
              <Box
                key={attachmentIndex}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <CustomImageCard
                  src={item.presignedUrl}
                  attachmentNumber={item.fileName}
                  attachmentType={item.fileType}
                  attachmentName={item.fileName}
                  cardMaxHeight={{ xs: 40, sm: 40, md: 40 }}
                  cardWidth={{ xs: '100%', sm: 70, md: 80 }}
                  cardMinHeight={40}
                  imageMaxHeight={15}
                />

                <IconButton
                  onClick={() => {
                    handleDeleteAttachment(
                      index,
                      purchaseDataIndex,
                      attachmentIndex,
                    )
                  }}
                  disabled={disabled}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: 20,
                      color: 'inherit',
                      visibility: disabled ? 'hidden' : null,
                    }}
                  />
                </IconButton>
              </Box>
            ))) ??
            null}
        </Box>
      </Grid2>
    </Grid2>
  )
}
